import { ErrorBoundary, IconButton, Tooltip } from "@prismicio/editor-ui"
import { Suspense } from "react"

import { canRoleArchiveDocument } from "../../app/services/permissions"
import {
  getTeamSpaceByCustomTypeId,
  useResolveMyRole,
  useTeamSpaces,
} from "../../app/services/role"
import { useBulkActionsButton } from "./useBulkActionsButton"
import { transformDocumentToBulkTaskItem } from "./utils"

interface ArchiveDocumentsButtonProps {
  /**
   * Provided when archiving documents in a release
   */
  releaseId?: string
}

export function ArchiveDocumentsButton(props: ArchiveDocumentsButtonProps) {
  const { releaseId } = props

  return (
    <ErrorBoundary renderError={Fallback}>
      <Suspense fallback={<Fallback />}>
        <ArchiveButton releaseId={releaseId} />
      </Suspense>
    </ErrorBoundary>
  )
}

function ArchiveButton(props: ArchiveDocumentsButtonProps) {
  const { releaseId } = props
  const { action, disabled, tooltip, selectedDocuments } =
    useBulkArchiveButton(releaseId)

  function onClick() {
    const items = selectedDocuments.map((document) =>
      transformDocumentToBulkTaskItem(document, releaseId),
    )

    action("archive", { items })
  }

  return (
    <Tooltip content={tooltip || "Archive"}>
      <IconButton
        icon="inventory"
        onClick={onClick}
        disabled={disabled}
        color="grey"
      />
    </Tooltip>
  )
}

function Fallback() {
  return <IconButton icon="inventory" disabled color="grey" />
}

function useBulkArchiveButton(releaseId?: string) {
  const {
    action,
    disabled: initialDisabled,
    tooltip: initialTooltip,
    selectedDocuments,
  } = useBulkActionsButton(releaseId)

  const resolveMyRole = useResolveMyRole()
  const teamSpaces = useTeamSpaces()

  const canArchiveDocuments = selectedDocuments.every((document) => {
    const version = document.versions[0]
    if (!version) return false

    const teamSpace = getTeamSpaceByCustomTypeId(
      teamSpaces,
      document.custom_type_id,
    )
    const role = resolveMyRole({
      localeId: document.language.id,
      teamSpaceId: teamSpace.id,
    })

    return canRoleArchiveDocument(role, version.status)
  })

  const disabled = initialDisabled || !canArchiveDocuments

  function getTooltip() {
    if (initialTooltip) {
      return initialTooltip
    }
    if (!canArchiveDocuments) {
      return "You don't have permission to archive some of the selected documents"
    }
    return undefined
  }

  return {
    action,
    disabled,
    tooltip: getTooltip(),
    selectedDocuments,
  }
}
